import { useEffect } from "react";
import { Link, Navigate, NavLink, useParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthProvider";
import { BrandLockup } from "../../brand/BrandMark";
import {
  companyDetails,
  findLegalDocument,
  formatEffective,
  type LegalSection,
  legalDocuments,
} from "../../legal/documents";
import { LegalFooter } from "../../legal/LegalFooter";
import { initials } from "../../lib/format";
import { PRODUCT_NAME } from "../../product";
import "./legal.css";

/**
 * The public legal pages: /legal lists the documents and /legal/:slug shows one.
 *
 * They sit outside the signed-in frame because a prospective customer, a venue's customer and a
 * security researcher all need them without an account. The words come from legal/documents.ts;
 * this file only lays them out.
 */

function Section({ section }: { section: LegalSection }) {
  const headers = section.table?.headers ?? [];
  return (
    <section>
      <h2>{section.heading}</h2>
      {section.paragraphs?.map((text) => (
        <p key={text}>{text}</p>
      ))}
      {section.bullets ? (
        <ul>
          {section.bullets.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : null}
      {section.table ? (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                {headers.map((header) => (
                  <th key={header} scope="col">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {section.table.rows.map((row) => (
                <tr key={row.join("|")}>
                  {headers.map((header) => (
                    <td key={header}>{row[headers.indexOf(header)]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

// What the header offers depends on who is reading. A visitor is offered the sign-in; somebody
// already signed in is shown who they are and offered the way back into the product, because a
// sign-in button in front of a signed-in person reads as a session that has been lost.
function AccountLink() {
  const auth = useAuth();
  if (auth.loading) return null;
  const user = auth.session?.user;
  if (!user) {
    return (
      <Link className="button secondary" to="/login">
        Sign in
      </Link>
    );
  }
  return (
    <Link className="legal-account" to="/">
      {user.avatarUrl ? (
        <img className="legal-account-avatar" src={user.avatarUrl} alt="" />
      ) : (
        <span className="legal-account-avatar" aria-hidden="true">
          {initials(user.displayName)}
        </span>
      )}
      <span className="legal-account-text">
        <strong>{user.displayName}</strong>
        <small>Back to {PRODUCT_NAME}</small>
      </span>
    </Link>
  );
}

export function LegalPage() {
  const { slug } = useParams();
  const entry = slug ? findLegalDocument(slug) : undefined;

  // Moving between documents is a route change inside one page, and the browser keeps the scroll
  // position across it, so the next document would open part-way down. Each one starts at the top,
  // the index included; an unknown address is sent to the index below and starts there.
  useEffect(() => {
    if (!slug || entry) window.scrollTo({ top: 0, left: 0 });
  }, [entry, slug]);

  if (slug && !entry) return <Navigate to="/legal" replace />;

  return (
    <div className="legal-page">
      <title>{`${entry ? entry.title : "Legal"} · ${PRODUCT_NAME}`}</title>
      <header className="legal-nav">
        <Link className="legal-brand" to="/" aria-label={`${PRODUCT_NAME} home`}>
          <BrandLockup />
        </Link>
        <nav aria-label="Public navigation">
          <AccountLink />
        </nav>
      </header>
      <div className="legal-layout">
        <nav className="legal-index" aria-label="Legal documents">
          <h2>Legal</h2>
          <ul>
            {legalDocuments.map((item) => (
              <li key={item.slug}>
                <NavLink to={`/legal/${item.slug}`} end>
                  {item.title}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <main className="legal-article" id="main">
          {entry ? (
            <article>
              <p className="eyebrow">{companyDetails.legalName}</p>
              <h1>{entry.title}</h1>
              <p className="legal-meta">
                Version {entry.version} · Effective {formatEffective(entry.effective)}
              </p>
              <p className="legal-summary">{entry.summary}</p>
              {entry.sections.map((section) => (
                <Section key={section.heading} section={section} />
              ))}
            </article>
          ) : (
            <article>
              <p className="eyebrow">{companyDetails.legalName}</p>
              <h1>Legal and trust</h1>
              <p className="legal-summary">
                Everything the company states about who runs the platform, how it treats personal
                data, what it promises customers and how to reach it, in one place.
              </p>
              <ul className="legal-list">
                {legalDocuments.map((item) => (
                  <li key={item.slug}>
                    <Link to={`/legal/${item.slug}`}>{item.title}</Link>
                    <p>{item.summary}</p>
                  </li>
                ))}
              </ul>
            </article>
          )}
        </main>
      </div>
      <LegalFooter />
    </div>
  );
}
